/**
 * Who a dashboard is shared with, and changing that.
 *
 * The catalog's sharing state, over dashboard grants. Two things are kept from
 * it deliberately:
 *
 * * **The list is re-read from the server after every change**, never patched
 *   locally. A grant that looks applied but was refused is the one bug in a
 *   share dialog nobody notices in time.
 * * **Every failure is said out loud.** Sharing is not a background nicety; a
 *   colleague who was never actually given access is worse than an error.
 *
 * After a change the browse row is refreshed too, so the "Shared · N" badge and
 * the dialog cannot disagree about what just happened.
 */

import {
  changeDashboardGrant,
  grantDashboard,
  listDashboardGrants,
  revokeDashboardGrant,
  searchDashboardPrincipals,
} from '../../api/endpoints.ts';
import type { DashboardGrant, PrincipalSearch } from '../../api/types.ts';
import { Atom } from '../../state/store.ts';
import { showToast } from '../../ui/toast.ts';
import { dashboards, refreshDashboardRow } from './dashboard-state.ts';
import { userMessage } from '../../api/errors.ts';

/** The dashboard the share dialog is open for, and its grants. */
export const sharingDashboardId = new Atom<string | null>(null);
export const dashboardGrants = new Atom<DashboardGrant[]>([]);
export const dashboardGrantsLoading = new Atom<boolean>(false);

function message(error: unknown): string {
  return userMessage(error);
}

/** The row the dialog draws its header from. */
export function sharingDashboard() {
  const id = sharingDashboardId.value;
  return id ? (dashboards.value.find((d) => d.id === id) ?? null) : null;
}

export async function openDashboardShareDialog(id: string): Promise<void> {
  sharingDashboardId.set(id);
  dashboardGrants.set([]);
  dashboardGrantsLoading.set(true);
  try {
    dashboardGrants.set(await listDashboardGrants(id));
  } catch (error) {
    showToast('error', `Could not read who this is shared with: ${message(error)}`);
  } finally {
    dashboardGrantsLoading.set(false);
  }
}

export function closeDashboardShareDialog(): void {
  sharingDashboardId.set(null);
  dashboardGrants.set([]);
}

/** Re-read the grants, and the row behind the badge, after a change. */
async function reload(id: string): Promise<void> {
  dashboardGrants.set(await listDashboardGrants(id));
  void refreshDashboardRow(id);
}

export async function shareDashboard(
  id: string,
  principal: { user_id?: string; group_id?: string },
  role: 'viewer' | 'editor',
  expiresAt?: string | null,
): Promise<boolean> {
  try {
    await grantDashboard(id, { ...principal, role, expires_at: expiresAt ?? null });
    await reload(id);
    return true;
  } catch (error) {
    showToast('error', `Could not share the dashboard: ${message(error)}`);
    return false;
  }
}

export async function revokeDashboardShare(id: string, grantId: string): Promise<boolean> {
  try {
    await revokeDashboardGrant(id, grantId);
    await reload(id);
    return true;
  } catch (error) {
    showToast('error', `Could not withdraw access: ${message(error)}`);
    return false;
  }
}

/** Make an existing grant viewer or editor. */
export async function changeDashboardShareRole(
  id: string,
  grantId: string,
  role: 'viewer' | 'editor',
): Promise<boolean> {
  try {
    await changeDashboardGrant(id, grantId, role);
    await reload(id);
    return true;
  } catch (error) {
    showToast('error', `Could not change their access: ${message(error)}`);
    return false;
  }
}

export async function findDashboardPrincipals(query: string): Promise<PrincipalSearch> {
  try {
    return await searchDashboardPrincipals(query);
  } catch {
    // A failed lookup leaves the picker empty rather than erroring the dialog:
    // the person can try again by typing, and nothing has been changed.
    return { users: [], groups: [] };
  }
}
